import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
// jsPDF loads on demand inside handleExportPDF — it's ~300KB and only Export uses it
import { supabase } from '../services/supabase'
import { calculateInches, calculateAcreInches } from '../utils/calculations'
import { formatReadingValue } from '../utils/constants'

const AppContext = createContext(null)

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}

export function AppProvider({ children, session, setSession }) {
  // Toast state
  const [toast, setToast] = useState(null)
  const toastTimerRef = useRef(null)
  // In-flight lock so a fast double/triple-tap can't insert the same reading twice.
  const [savingReading, setSavingReading] = useState(false)
  const savingReadingRef = useRef(false)
  const toastExitRef = useRef(null)
  const showToast = useCallback((message, type = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    if (toastExitRef.current) clearTimeout(toastExitRef.current)
    setToast({ message, type, exiting: false })
    toastTimerRef.current = setTimeout(() => {
      setToast(prev => prev ? { ...prev, exiting: true } : null)
      toastExitRef.current = setTimeout(() => setToast(null), 200)
    }, 2500)
  }, [])

  // Core state
  const [loading, setLoading] = useState(true)
  const [operation, setOperation] = useState(null)
  const [meters, setMeters] = useState([])
  const [readings, setReadings] = useState([])

  // UI state - Add Reading page
  const [mode, setMode] = useState('record')
  const [selectedMeterId, setSelectedMeterId] = useState('')
  const [enteredBy, setEnteredBy] = useState('')
  const [newReading, setNewReading] = useState({ start: '', end: '', notes: '' })
  const [readingDateTime, setReadingDateTime] = useState('')
  const [calcAcres, setCalcAcres] = useState('')
  const [calcUnits, setCalcUnits] = useState('Gallons × 100')

  // Modals state
  const [showAddMeter, setShowAddMeter] = useState(false)
  const [showQuickAddMeter, setShowQuickAddMeter] = useState(false)
  const [showRolloverModal, setShowRolloverModal] = useState(false)
  const [showSeasonStartConfirmModal, setShowSeasonStartConfirmModal] = useState(false)
  const [showSeasonPrompt, setShowSeasonPrompt] = useState(false)
  const [showMeterDetails, setShowMeterDetails] = useState(false)
  const [meterDetailsId, setMeterDetailsId] = useState(null)
  const [photoPreviewModal, setPhotoPreviewModal] = useState(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showDecimalConfirmModal, setShowDecimalConfirmModal] = useState(false)
  const [exportProgress, setExportProgress] = useState(null)

  // Meter form state
  const [newMeter, setNewMeter] = useState({
    name: '', acres: '', units: '', brand: '', serialNumber: '',
    notes: '', wellOutputGpm: '', isSeasonStart: false, seasonStartReading: ''
  })
  const [editingMeterId, setEditingMeterId] = useState(null)
  const [editingMeter, setEditingMeter] = useState(null)
  const [editingReadingId, setEditingReadingId] = useState(null)
  const [editingReading, setEditingReading] = useState(null)

  // Meters page state
  const [meterSearch, setMeterSearch] = useState('')
  const [showMeterSearch, setShowMeterSearch] = useState(false)
  const [selectedMetric, setSelectedMetric] = useState('seasonTotal')
  const [sortBy, setSortBy] = useState('name')
  const [showFilterControls, setShowFilterControls] = useState(false)
  const [filterUnits, setFilterUnits] = useState('')
  const [expandedMeterId, setExpandedMeterId] = useState(null)

  // Settings page state
  const [expandedSettingsSection, setExpandedSettingsSection] = useState(null)
  const [editingOpName, setEditingOpName] = useState(false)
  const [newOpName, setNewOpName] = useState('')
  const [editingFirstName, setEditingFirstName] = useState(false)
  const [newFirstName, setNewFirstName] = useState('')
  const [editingSeasonDates, setEditingSeasonDates] = useState(false)
  const [seasonStart, setSeasonStart] = useState('')
  const [seasonEnd, setSeasonEnd] = useState('')
  const [teamMembers, setTeamMembers] = useState([])
  const [inviteCode, setInviteCode] = useState(null)

  // Season start from Add Reading
  const [markAsSeasonStart, setMarkAsSeasonStart] = useState(false)

  // Photo state
  const [readingPhotoFile, setReadingPhotoFile] = useState(null)
  const [readingPhotoPreview, setReadingPhotoPreview] = useState(null)
  const [readingPhotoUrls, setReadingPhotoUrls] = useState({})
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const isOwner = session?.user?.id === operation?.owner_id

  // ============================================================================
  // DATA LOADING FUNCTIONS
  // ============================================================================

  const loadMeters = async (opId) => {
    const { data, error } = await supabase
      .from("meters")
      .select("id,created_at,owner_id,operation_id,name,acres,units,brand,serial_number,notes,season_starting_reading,season_starting_choice,season_starting_date,well_output_gpm")
      .eq("operation_id", opId)
      .order("created_at")
    if (error) throw error
    setMeters(data || [])
  }

  const loadPhotoUrl = async (photoPath) => {
    if (!photoPath) return null
    try {
      const { data, error } = await supabase.storage
        .from('meter_photos')
        .createSignedUrl(photoPath, 3600)
      if (error) throw error
      return data?.signedUrl || null
    } catch (e) {
      console.error('Error loading photo URL:', e)
      return null
    }
  }

  const loadAllReadingPhotoUrls = async (readingsData) => {
    // One batch call signs every photo URL at once (the old loop made one
    // round trip per photo, in series — slow on rural signal).
    const withPhotos = readingsData.filter(r => r.photo_path)
    if (withPhotos.length === 0) {
      setReadingPhotoUrls({})
      return
    }
    const urls = {}
    try {
      const { data, error } = await supabase.storage
        .from('meter_photos')
        .createSignedUrls(withPhotos.map(r => r.photo_path), 3600)
      if (error) throw error
      const byPath = {}
      for (const item of data || []) {
        if (item.signedUrl) byPath[item.path] = item.signedUrl
      }
      for (const r of withPhotos) {
        if (byPath[r.photo_path]) urls[r.id] = byPath[r.photo_path]
      }
    } catch (e) {
      console.error('Error loading photo URLs:', e)
      // fall back to per-photo requests so photos still load
      for (const r of withPhotos) {
        const url = await loadPhotoUrl(r.photo_path)
        if (url) urls[r.id] = url
      }
    }
    setReadingPhotoUrls(urls)
  }

  const loadReadings = async (opId, seasonStartDate = null, seasonEndDate = null) => {
    let query = supabase
      .from("readings")
      .select("*")
      .eq("operation_id", opId)

    if (seasonStartDate) query = query.gte("created_at", seasonStartDate)
    if (seasonEndDate) query = query.lte("created_at", seasonEndDate)

    query = query.order("created_at", { ascending: true })

    const { data, error } = await query
    if (error) throw error
    setReadings(data || [])

    if (data && data.length > 0) {
      await loadAllReadingPhotoUrls(data)
    }
  }

  const loadTeamMembers = async (opId, ownerId) => {
    try {
      const { data, error } = await supabase
        .from("operation_members")
        .select("user_id, email")
        .eq("operation_id", opId)
      if (error) throw error

      setTeamMembers([
        { user_id: ownerId, email: session?.user?.email, role: "owner" },
        ...(data || []).map(m => ({ ...m, role: "member" }))
      ])
    } catch (e) {
      console.error("Error loading team members:", e)
    }
  }

  const loadOrCreateInviteCode = async (operationId, userId, operationName) => {
    try {
      const { data: existingInvites, error: queryError } = await supabase
        .from("operation_invites")
        .select("invite_code")
        .eq("operation_id", operationId)
        .order("created_at", { ascending: false })
        .limit(1)

      if (queryError) throw queryError

      if (existingInvites && existingInvites.length > 0) {
        setInviteCode(existingInvites[0].invite_code)
      } else {
        const code = Math.random().toString(36).substring(2, 10).toUpperCase()
        const { error: insertError } = await supabase
          .from("operation_invites")
          .insert([{
            invite_code: code,
            operation_id: operationId,
            created_by: userId,
            operation_name: operationName
          }])
        if (insertError) throw insertError
        setInviteCode(code)
      }
    } catch (e) {
      console.error("Error loading/creating invite code:", e)
    }
  }

  const ensureOperation = async (user) => {
    const { data: memberOps, error: memberErr } = await supabase
      .from("operation_members")
      .select("operation_id")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(1)

    if (memberErr) throw memberErr

    if (memberOps && memberOps.length) {
      const { data: op, error: getOpErr } = await supabase
        .from("operations")
        .select("*")
        .eq("id", memberOps[0].operation_id)
        .single()
      if (getOpErr) throw getOpErr
      return op
    }

    const { data: ownedOps, error: opErr } = await supabase
      .from("operations")
      .select("*")
      .eq("owner_id", user.id)
      .limit(1)

    if (opErr) throw opErr
    if (ownedOps && ownedOps.length) return ownedOps[0]

    const defaultName = (user.email?.split("@")[0] || "My") + " Operation"
    const currentYear = new Date().getFullYear()

    const { data: created, error: insErr } = await supabase
      .from("operations")
      .insert([{
        owner_id: user.id,
        op_name: defaultName,
        season_start: `${currentYear}-01-01`,
        season_end: `${currentYear}-12-31`
      }])
      .select("*")
      .single()

    if (insErr) throw insErr
    created._isNew = true
    return created
  }

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // One pass over readings -> per-meter list sorted newest-first. Every getter
  // below reads this index; nothing re-filters or re-sorts the whole array.
  const readingsByMeter = useMemo(() => {
    const map = new Map()
    for (const r of readings) {
      const list = map.get(r.meter_id)
      if (list) list.push(r)
      else map.set(r.meter_id, [r])
    }
    for (const list of map.values()) {
      list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }
    return map
  }, [readings])

  // Returns the meter's readings NEWEST-FIRST (callers that re-sort still work).
  const getMeterReadings = useCallback((meterId) => readingsByMeter.get(meterId) || [], [readingsByMeter])

  const getSeasonTotal = useCallback((meterId) => {
    const meter = meters.find(m => m.id === meterId)
    if (!meter) return '-'
    if (meter.season_starting_reading === null || meter.season_starting_reading === undefined) return '-'

    const latest = getMeterReadings(meterId)[0]
    if (!latest) return '0.00'
    return parseFloat(calculateInches(
      meter.season_starting_reading,
      latest.end,
      meter.acres,
      meter.units
    )).toFixed(2)
  }, [meters, getMeterReadings])

  const getLastIrrigation = useCallback((meterId) => {
    const latest = getMeterReadings(meterId)[0]
    if (!latest) return '0.00'
    return (latest.inches ?? 0).toString()
  }, [getMeterReadings])

  const getEntriesThisSeason = useCallback((meterId) => getMeterReadings(meterId).length.toString(), [getMeterReadings])

  const getLastReadingDate = useCallback((meterId) => {
    const latest = getMeterReadings(meterId)[0]
    return latest ? latest.created_at : null
  }, [getMeterReadings])

  const isReadingOutsideSeason = useCallback((dateTimeValue) => {
    if (!dateTimeValue || !operation?.season_start || !operation?.season_end) return false
    const readingDate = new Date(dateTimeValue)
    const start = new Date(operation.season_start + 'T00:00:00')
    const end = new Date(operation.season_end + 'T23:59:59')
    return readingDate < start || readingDate > end
  }, [operation?.season_start, operation?.season_end])

  const selectedMeter = meters.find(m => m.id === selectedMeterId)
  const lastReading = selectedMeter ? (getMeterReadings(selectedMeterId)[0] || null) : null

  // Show "Set as season start" checkbox when meter has no season start and no readings
  const showSeasonStartCheckbox = selectedMeter
    && (selectedMeter.season_starting_reading == null)
    && !lastReading

  // Auto-check when conditions are met, auto-uncheck when they're not
  useEffect(() => {
    setMarkAsSeasonStart(!!showSeasonStartCheckbox)
  }, [showSeasonStartCheckbox])

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================

  const resizeImage = (file, maxWidth = 1200) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let width = img.width
          let height = img.height
          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }
          canvas.width = width
          canvas.height = height
          canvas.getContext('2d').drawImage(img, 0, 0, width, height)
          canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9)
        }
        img.onerror = reject
        img.src = e.target.result
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const uploadReadingPhoto = async (readingId, file) => {
    if (!readingId || !file) return null
    try {
      const resizedBlob = await resizeImage(file, 1200)
      const path = `readings/${readingId}.jpg`
      const { error } = await supabase.storage
        .from('meter_photos')
        .upload(path, resizedBlob, { upsert: true, contentType: 'image/jpeg' })
      if (error) throw error
      return path
    } catch (e) {
      console.error('Error uploading photo:', e)
      throw e
    }
  }

  const handleReadingPhotoSelect = (e) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setReadingPhotoFile(file)
      setReadingPhotoPreview(URL.createObjectURL(file))
    }
  }

  // Free the previous preview's memory whenever it changes or unmounts —
  // object URLs otherwise pile up over a long PWA session.
  useEffect(() => {
    const url = readingPhotoPreview
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [readingPhotoPreview])

  const resetNewMeter = () => {
    setNewMeter({
      name: '', acres: '', units: '', brand: '', serialNumber: '',
      notes: '', wellOutputGpm: '', isSeasonStart: false, seasonStartReading: ''
    })
  }

  const handleAddMeter = async () => {
    if (!session?.user || !operation) return
    if (!newMeter.name || !newMeter.acres) {
      showToast('Please fill in meter name and acres', 'error')
      return
    }
    if (!newMeter.units) {
      showToast('Please select meter units', 'error')
      return
    }
    if (newMeter.isSeasonStart && !newMeter.seasonStartReading) {
      showToast('Please enter the season starting reading', 'error')
      return
    }

    try {
      const meterData = {
        owner_id: session.user.id,
        operation_id: operation.id,
        name: newMeter.name,
        acres: newMeter.acres,
        units: newMeter.units,
        brand: newMeter.brand || null,
        serial_number: newMeter.serialNumber || null,
        notes: newMeter.notes || null,
        well_output_gpm: newMeter.wellOutputGpm || null
      }

      if (newMeter.isSeasonStart && newMeter.seasonStartReading) {
        meterData.season_starting_reading = parseFloat(newMeter.seasonStartReading)
        meterData.season_starting_choice = 'manual'
        meterData.season_starting_date = new Date().toISOString()
      }

      const { data, error } = await supabase
        .from("meters")
        .insert([meterData])
        .select()
        .single()
      if (error) throw error

      // Drop in the season-start baseline reading so the meter counts usage from
      // this number. Without it, the first field reading gets treated as the
      // baseline (0" that day) instead of measuring against the start.
      if (data?.id && meterData.season_starting_reading != null) {
        const seasonDate = operation?.season_start ? `${operation.season_start}T12:00:00` : new Date().toISOString()
        const { error: baselineError } = await supabase
          .from("readings")
          .insert([{
            owner_id: session.user.id,
            operation_id: operation.id,
            meter_id: data.id,
            start: meterData.season_starting_reading,
            end: meterData.season_starting_reading,
            inches: 0,
            entered_by: session?.user?.user_metadata?.first_name || 'System',
            notes: 'Season start reading',
            created_at: seasonDate,
          }])
        if (baselineError) console.error('Error creating season-start baseline reading:', baselineError)
      }

      resetNewMeter()
      setShowAddMeter(false)

      if (showQuickAddMeter) {
        setShowQuickAddMeter(false)
        setSelectedMeterId(data?.id)
      }

      await loadMeters(operation.id)
      showToast('Meter added', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Failed to add meter', 'error')
    }
  }

  const saveReading = async () => {
    if (!session?.user || !operation) return
    if (savingReadingRef.current) return // already saving — ignore repeat taps

    const meter = meters.find(m => m.id === selectedMeterId)
    if (!meter) {
      showToast("Please select a meter", "error")
      return
    }

    savingReadingRef.current = true
    setSavingReading(true)

    const isFirstReading = !lastReading
    const effectiveStart = isFirstReading ? newReading.end : lastReading.end
    const inches = isFirstReading ? 0 : calculateInches(effectiveStart, newReading.end, meter.acres, meter.units)

    try {
      const { data: insertedData, error } = await supabase
        .from("readings")
        .insert([{
          owner_id: session.user.id,
          operation_id: operation.id,
          meter_id: meter.id,
          start: effectiveStart,
          end: newReading.end,
          inches: parseFloat(inches),
          entered_by: enteredBy.trim() || session?.user?.user_metadata?.first_name || session?.user?.email || 'Unknown',
          notes: newReading.notes || null,
          created_at: readingDateTime ? new Date(readingDateTime).toISOString() : new Date().toISOString()
        }])
        .select()
      if (error) throw error

      if (readingPhotoFile && insertedData?.[0]) {
        try {
          setUploadingPhoto(true)
          const photoPath = await uploadReadingPhoto(insertedData[0].id, readingPhotoFile)
          if (photoPath) {
            await supabase.from("readings").update({ photo_path: photoPath }).eq("id", insertedData[0].id)
          }
        } catch (photoError) {
          console.error('Error uploading photo:', photoError)
          showToast('Reading saved but photo upload failed', 'warning')
        } finally {
          setUploadingPhoto(false)
        }
      }

      // Set season starting reading on the meter if flagged
      if (markAsSeasonStart) {
        const { error: meterError } = await supabase
          .from("meters")
          .update({
            season_starting_reading: parseFloat(newReading.end),
            season_starting_choice: 'manual',
            season_starting_date: readingDateTime ? new Date(readingDateTime).toISOString() : new Date().toISOString()
          })
          .eq("id", meter.id)
        if (meterError) console.error('Error setting season start:', meterError)
        await loadMeters(operation.id)
      }

      setNewReading({ start: '', end: '', notes: '' })
      setSelectedMeterId(null)
      setReadingPhotoFile(null)
      setReadingPhotoPreview(null)

      const now = new Date()
      setReadingDateTime(new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16))

      await loadReadings(operation.id, operation.season_start, operation.season_end)
      showToast('Reading saved', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error saving reading', 'error')
    } finally {
      savingReadingRef.current = false
      setSavingReading(false)
    }
  }

  const saveFirstReading = async (meterId, readingValue, notes, photoFile, options = {}) => {
    if (!session?.user || !operation) return

    const meter = meters.find(m => m.id === meterId)
    if (!meter) {
      showToast("Meter not found", "error")
      return
    }

    const resolvedEnteredBy = (options.enteredBy || enteredBy || '').trim() || session?.user?.user_metadata?.first_name || 'Unknown'
    const resolvedCreatedAt = options.dateTime ? new Date(options.dateTime).toISOString() : new Date().toISOString()

    try {
      const { data: insertedData, error } = await supabase
        .from("readings")
        .insert([{
          owner_id: session.user.id,
          operation_id: operation.id,
          meter_id: meterId,
          start: readingValue,
          end: readingValue,
          inches: 0,
          entered_by: resolvedEnteredBy,
          notes: notes || null,
          created_at: resolvedCreatedAt
        }])
        .select()
      if (error) throw error

      if (photoFile && insertedData?.[0]) {
        try {
          setUploadingPhoto(true)
          const photoPath = await uploadReadingPhoto(insertedData[0].id, photoFile)
          if (photoPath) {
            await supabase.from("readings").update({ photo_path: photoPath }).eq("id", insertedData[0].id)
          }
        } catch (photoError) {
          console.error('Error uploading photo:', photoError)
          showToast('Reading saved but photo upload failed', 'warning')
        } finally {
          setUploadingPhoto(false)
        }
      }

      const { error: meterError } = await supabase
        .from("meters")
        .update({
          season_starting_reading: parseFloat(readingValue),
          season_starting_choice: 'manual',
          season_starting_date: resolvedCreatedAt
        })
        .eq("id", meterId)
      if (meterError) console.error('Error setting season start:', meterError)

      await loadMeters(operation.id)
      await loadReadings(operation.id, operation.season_start, operation.season_end)
      showToast('Reading saved', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error saving reading', 'error')
    }
  }

  const handleAddReading = async () => {
    if (!session?.user || !operation) return

    const meter = meters.find(m => m.id === selectedMeterId)
    if (!meter) {
      showToast("Please select a meter", "error")
      return
    }

    const previousValue = lastReading?.end ?? meter.season_starting_reading
    if (previousValue != null && parseFloat(newReading.end) < parseFloat(previousValue)) {
      setShowRolloverModal(true)
      return
    }

    await saveReading()
  }

  const handleEditMeter = async () => {
    if (!editingMeter) return
    if (!editingMeter.units) {
      showToast('Please select meter units', 'error')
      return
    }
    try {
      const { error } = await supabase
        .from("meters")
        .update({
          name: editingMeter.name,
          acres: editingMeter.acres,
          units: editingMeter.units,
          brand: editingMeter.brand || null,
          serial_number: editingMeter.serial_number || null,
          notes: editingMeter.notes || null,
          well_output_gpm: editingMeter.well_output_gpm || null
        })
        .eq("id", editingMeterId)
      if (error) throw error

      setEditingMeterId(null)
      setEditingMeter(null)
      await loadMeters(operation.id)
      showToast('Meter updated', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error updating meter', 'error')
    }
  }

  const handleDeleteReading = async (readingId) => {
    if (!confirm('Delete this reading?')) return
    try {
      const { error } = await supabase.from("readings").delete().eq("id", readingId)
      if (error) throw error
      await loadReadings(operation.id, operation.season_start, operation.season_end)
      showToast('Reading deleted', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error deleting reading', 'error')
    }
  }

  const handleUpdateReading = async (photoFile) => {
    if (!editingReading) return
    try {
      const meter = meters.find(m => m.id === editingReading.meter_id)
      const inches = meter ? parseFloat(calculateInches(editingReading.start, editingReading.end, meter.acres, meter.units)) : editingReading.inches
      const { error } = await supabase
        .from("readings")
        .update({
          end: editingReading.end,
          inches,
          created_at: editingReading.created_at
        })
        .eq("id", editingReadingId)
      if (error) throw error

      if (photoFile) {
        const photoPath = await uploadReadingPhoto(editingReadingId, photoFile)
        if (photoPath) {
          await supabase.from("readings").update({ photo_path: photoPath }).eq("id", editingReadingId)
        }
      }

      setEditingReadingId(null)
      setEditingReading(null)
      await loadReadings(operation.id, operation.season_start, operation.season_end)
      showToast('Reading updated', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error updating reading', 'error')
    }
  }

  const handleDeleteMeter = async (meterId) => {
    if (!confirm('Delete this meter and all its readings? This cannot be undone.')) return
    try {
      // First delete all readings for this meter
      const { error: readingsError } = await supabase
        .from("readings")
        .delete()
        .eq("meter_id", meterId)
      if (readingsError) throw readingsError

      // Then delete the meter
      const { error: meterError } = await supabase
        .from("meters")
        .delete()
        .eq("id", meterId)
      if (meterError) throw meterError

      // Close any open modals
      setShowMeterDetails(false)
      setMeterDetailsId(null)
      setExpandedMeterId(null)

      await Promise.all([
        loadMeters(operation.id),
        loadReadings(operation.id, operation.season_start, operation.season_end)
      ])
      showToast('Meter deleted', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error deleting meter', 'error')
    }
  }

  const handleUpdateSeasonStartReading = async (meterId, value, date) => {
    try {
      const seasonDate = value !== '' ? (date ? new Date(date).toISOString() : new Date().toISOString()) : null
      const { error } = await supabase
        .from("meters")
        .update({
          season_starting_reading: value !== '' ? parseFloat(value) : null,
          season_starting_choice: value !== '' ? 'manual' : null,
          season_starting_date: seasonDate
        })
        .eq("id", meterId)
      if (error) throw error
      await loadMeters(operation.id)
      showToast('Season starting reading updated', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error updating meter', 'error')
    }
  }

  const handleUpdateOpName = async () => {
    if (!newOpName.trim()) return
    if (!confirm(`Change operation name to "${newOpName}"?`)) return
    try {
      const { error } = await supabase
        .from("operations")
        .update({ op_name: newOpName.trim() })
        .eq("id", operation.id)
      if (error) throw error
      setOperation({...operation, op_name: newOpName.trim()})
      setEditingOpName(false)
      setNewOpName('')
      showToast('Operation name updated', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error updating operation', 'error')
    }
  }

  const handleUpdateSeasonDates = async (directStart, directEnd) => {
    if (!operation) return
    const start = directStart !== undefined ? directStart : seasonStart
    const end = directEnd !== undefined ? directEnd : seasonEnd
    try {
      const { error } = await supabase
        .from("operations")
        .update({ season_start: start || null, season_end: end || null })
        .eq("id", operation.id)
      if (error) throw error
      setOperation({...operation, season_start: start || null, season_end: end || null})
      setSeasonStart(start || '')
      setSeasonEnd(end || '')
      setEditingSeasonDates(false)
      await loadReadings(operation.id, start || null, end || null)
      showToast('Season dates updated', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error updating season dates', 'error')
    }
  }

  const handleUpdateFirstName = async () => {
    if (!newFirstName.trim()) {
      showToast('Please enter a name', 'error')
      return
    }
    try {
      const { error } = await supabase.auth.updateUser({ data: { first_name: newFirstName.trim() } })
      if (error) throw error
      setEditingFirstName(false)
      setNewFirstName('')
      showToast('Name updated', 'success')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Failed to update name', 'error')
    }
  }

  const confirmSeasonStartFromPreviousSeason = () => {
    if (!operation?.season_start) {
      showToast('Please set season start date first', 'error')
      return
    }
    setShowSeasonStartConfirmModal(true)
  }

  const handleBulkSetSeasonStartFromPreviousSeason = async () => {
    setShowSeasonStartConfirmModal(false)
    const seasonStartDate = operation.season_start ? `${operation.season_start}T12:00:00` : new Date().toISOString()
    try {
      let updatedCount = 0
      let skippedCount = 0

      for (const meter of meters) {
        const { data: previousSeasonReadings, error: queryError } = await supabase
          .from("readings")
          .select("*")
          .eq("meter_id", meter.id)
          .lt("created_at", operation.season_start)
          .order("created_at", { ascending: false })
          .limit(1)

        if (queryError) continue

        if (previousSeasonReadings?.length > 0) {
          const readingValue = parseFloat(previousSeasonReadings[0].end)

          // Update meter metadata
          const { error: updateError } = await supabase
            .from("meters")
            .update({
              season_starting_reading: readingValue,
              season_starting_choice: 'previous_season',
              season_starting_date: seasonStartDate
            })
            .eq("id", meter.id)

          if (updateError) continue

          // Create a reading record for the season start
          await supabase
            .from("readings")
            .insert([{
              owner_id: session.user.id,
              operation_id: operation.id,
              meter_id: meter.id,
              start: readingValue,
              end: readingValue,
              inches: 0,
              entered_by: session?.user?.user_metadata?.first_name || 'System',
              notes: 'Carried over from previous season\'s final reading',
              created_at: seasonStartDate
            }])

          updatedCount++
        } else {
          skippedCount++
        }
      }

      await loadMeters(operation.id)
      await loadReadings(operation.id, operation.season_start, operation.season_end)

      if (updatedCount > 0) {
        showToast(`Updated ${updatedCount} meter(s)${skippedCount > 0 ? `, skipped ${skippedCount} (no previous readings)` : ''}`, 'success')
      } else {
        showToast('No meters had previous season readings', 'warning')
      }
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error updating meters', 'error')
    }
  }

  const backfillSeasonStartReadings = async () => {
    if (!session?.user || !operation) return

    const seasonStartDate = operation.season_start ? `${operation.season_start}T12:00:00` : new Date().toISOString()
    const correctNotes = 'Carried over from previous season\'s final reading'

    try {
      let createdCount = 0
      let updatedCount = 0
      let skippedCount = 0

      for (const meter of meters) {
        if (meter.season_starting_reading == null) {
          skippedCount++
          continue
        }

        const meterReadings = readings.filter(r => r.meter_id === meter.id)
        const matchingReading = meterReadings.find(r =>
          parseFloat(r.end) == parseFloat(meter.season_starting_reading) &&
          parseFloat(r.start) == parseFloat(r.end) &&
          parseFloat(r.inches) === 0
        )

        if (matchingReading) {
          // Fix existing backfilled readings with old notes/dates
          const existingDate = new Date(matchingReading.created_at).toLocaleDateString()
          const targetDate = new Date(seasonStartDate).toLocaleDateString()
          const needsUpdate = matchingReading.notes !== correctNotes || existingDate !== targetDate
          if (needsUpdate) {
            const { error } = await supabase
              .from("readings")
              .update({ notes: correctNotes, created_at: seasonStartDate })
              .eq("id", matchingReading.id)
            if (!error) updatedCount++
          } else {
            skippedCount++
          }
          continue
        }

        // Create a reading record for this season start
        const { error } = await supabase
          .from("readings")
          .insert([{
            owner_id: session.user.id,
            operation_id: operation.id,
            meter_id: meter.id,
            start: meter.season_starting_reading,
            end: meter.season_starting_reading,
            inches: 0,
            entered_by: session?.user?.user_metadata?.first_name || 'System',
            notes: correctNotes,
            created_at: seasonStartDate
          }])

        if (error) {
          console.error(`Error backfilling meter ${meter.name}:`, error)
          continue
        }

        createdCount++
      }

      await loadReadings(operation.id, operation.season_start, operation.season_end)
      const parts = []
      if (createdCount > 0) parts.push(`Created ${createdCount}`)
      if (updatedCount > 0) parts.push(`updated ${updatedCount}`)
      if (skippedCount > 0) parts.push(`${skippedCount} already correct`)
      showToast(parts.join(', ') || 'All season starts already have readings', createdCount > 0 || updatedCount > 0 ? 'success' : 'info')
    } catch (e) {
      console.error(e)
      showToast(e.message || 'Error backfilling readings', 'error')
    }
  }

  const dismissSeasonPrompt = () => {
    if (operation) {
      const dismissKey = `season_prompt_dismissed_${operation.id}_${operation.season_start}`
      localStorage.setItem(dismissKey, 'true')
    }
    setShowSeasonPrompt(false)
  }

  const handleSeasonPromptCarryOver = async () => {
    dismissSeasonPrompt()
    await handleBulkSetSeasonStartFromPreviousSeason()
  }

  const shareInviteLink = async () => {
    if (!inviteCode) return
    const link = `${window.location.origin}?invite=${inviteCode}`
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join MeterCalc', url: link })
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Share failed:', err)
      }
    } else {
      try {
        await navigator.clipboard.writeText(link)
        showToast("Invite link copied!", "success")
      } catch (e) {
        showToast("Failed to copy link", "error")
      }
    }
  }

  const removeTeamMember = async (userId) => {
    if (!operation) return
    if (!confirm("Remove this team member?")) return
    try {
      const { data: deleted, error } = await supabase
        .from("operation_members")
        .delete()
        .eq("operation_id", operation.id)
        .eq("user_id", userId)
        .select()
      if (error) throw error
      if (!deleted || deleted.length === 0) throw new Error("Could not remove member — check database permissions")

      // Regenerate invite code so the removed member can't rejoin with the old link
      const { data: deletedInvites } = await supabase
        .from("operation_invites")
        .delete()
        .eq("operation_id", operation.id)
        .select()
      if (!deletedInvites || deletedInvites.length === 0) {
        console.warn("Could not delete old invite code — check RLS policies on operation_invites")
      }
      const newCode = Math.random().toString(36).substring(2, 10).toUpperCase()
      await supabase
        .from("operation_invites")
        .insert([{
          invite_code: newCode,
          operation_id: operation.id,
          created_by: operation.owner_id,
          operation_name: operation.op_name
        }])
      setInviteCode(newCode)

      await loadTeamMembers(operation.id, operation.owner_id)
      showToast("Team member removed. Invite link has been reset.", "success")
    } catch (e) {
      console.error(e)
      showToast(e.message || "Failed to remove team member", "error")
    }
  }

  const doLogout = async () => {
    await supabase.auth.signOut()
    setOperation(null)
    setMeters([])
    setReadings([])
    setSelectedMeterId("")
    setMode('record')
    setSession(null)
  }

  const csvCell = (value) => {
    let str = String(value ?? '')
    str = str.replace(/"/g, '""')
    if (/^[=+\-@]/.test(str)) str = "'" + str
    return '"' + str + '"'
  }

  const handleExportCSV = () => {
    if (!readings.length) {
      showToast('No readings to export', 'error')
      return
    }
    let csv = 'Date,Meter,Previous Reading,Current Reading,Inches Applied,Acre-Inches,Entered By,Notes\n'
    readings.forEach(r => {
      const meter = meters.find(m => m.id === r.meter_id)
      const acreInches = calculateAcreInches(r.inches, meter?.acres || 0)
      const dateStr = r.created_at ? new Date(r.created_at).toISOString().split('T')[0] : ''
      const isSeasonStart = parseFloat(r.start) === parseFloat(r.end) && parseFloat(r.inches) === 0
      csv += [csvCell(dateStr), csvCell(meter?.name || 'Unknown'), csvCell(isSeasonStart ? '' : r.start), csvCell(r.end), csvCell(isSeasonStart ? '' : r.inches), csvCell(isSeasonStart ? '' : acreInches), csvCell(r.entered_by), csvCell(r.notes || '')].join(',') + '\n'
    })
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `metercalc-export-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    // revoke after the download has started — revoking synchronously can
    // cancel the save on some browsers (Safari/iOS)
    setTimeout(() => window.URL.revokeObjectURL(url), 10_000)
    showToast('CSV exported successfully', 'success')
  }

  const handleExportPDF = async (includePhotos = false) => {
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF()
    let yPos = 20
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 15
    const lineHeight = 7
    const photoThumbnailWidth = 50.8
    const photoMargin = 3

    let photoUrlCache = {}
    if (includePhotos) {
      const photosToLoad = readings.filter(r => r.photo_path)
      const totalPhotos = photosToLoad.length
      setExportProgress({ step: 'Loading photos', current: 0, total: totalPhotos })
      for (let i = 0; i < photosToLoad.length; i++) {
        const reading = photosToLoad[i]
        setExportProgress({ step: 'Loading photos', current: i + 1, total: totalPhotos })
        const url = await loadPhotoUrl(reading.photo_path)
        if (url) photoUrlCache[reading.id] = url
      }
    }
    setExportProgress({ step: 'Building PDF', current: 0, total: 0 })

    const loadImageAsBase64 = async (url) => {
      return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          let width = img.width
          let height = img.height
          const maxWidth = 800
          const maxHeight = 800
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth }
          if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight }
          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)
          resolve({ data: canvas.toDataURL('image/jpeg', 0.85), aspectRatio: width / height })
        }
        img.onerror = () => resolve(null)
        img.src = url
      })
    }

    doc.setFontSize(18)
    doc.setFont(undefined, 'bold')
    doc.text(operation?.op_name || 'Irrigation Report', margin, yPos)
    yPos += 10

    doc.setFontSize(10)
    doc.setFont(undefined, 'normal')
    const dateStr = new Date().toLocaleDateString()
    doc.text(`Generated: ${dateStr}`, margin, yPos)
    yPos += 3
    if (operation?.season_start && operation?.season_end) {
      doc.text(`Season: ${operation.season_start} to ${operation.season_end}`, margin, yPos)
      yPos += 10
    } else {
      yPos += 7
    }

    const sortedMeters = [...meters].sort((a, b) => a.name.localeCompare(b.name))
    for (let meterIndex = 0; meterIndex < sortedMeters.length; meterIndex++) {
      const meter = sortedMeters[meterIndex]
      const meterReadings = getMeterReadings(meter.id)
      const seasonTotal = getSeasonTotal(meter.id)

      // Keep the field name, total, metadata and first table row together.
      const headerHeight = lineHeight + 8 + 5 + (meter.serial_number ? 5 : 0) + 3 + 10
      if ((includePhotos && meterIndex > 0) || yPos + headerHeight > 275) {
        doc.addPage()
        yPos = 20
      }

      doc.setFontSize(14)
      doc.setFont(undefined, 'bold')
      doc.text(meter.name, margin, yPos)
      yPos += lineHeight

      // Match the field card's baseline-to-latest total, not the sum of
      // rounded reading rows (which can also contain overlapping intervals).
      doc.setFontSize(10)
      const totalLabel = seasonTotal === '-'
        ? 'Season total: N/A (no season starting reading)'
        : meterReadings.length === 0
          ? 'Season total: N/A (no readings recorded)'
          : `Season total: ${seasonTotal} inches  |  ${calculateAcreInches(seasonTotal, meter.acres)} acre-inches`
      doc.text(totalLabel, margin, yPos)
      yPos += 8

      doc.setFontSize(9)
      doc.setFont(undefined, 'normal')
      doc.text(`Acres: ${meter.acres}  |  Units: ${meter.units}  |  Brand: ${meter.brand || 'N/A'}`, margin, yPos)
      yPos += 5

      if (meter.serial_number) {
        doc.text(`Serial Number: ${meter.serial_number}`, margin, yPos)
        yPos += 5
      }

      if (meter.notes) {
        doc.setFont(undefined, 'italic')
        const noteLines = doc.splitTextToSize(`Notes: ${meter.notes}`, pageWidth - margin * 2)
        noteLines.forEach(line => {
          if (yPos > 270) { doc.addPage(); yPos = 20 }
          doc.text(line, margin, yPos)
          yPos += 5
        })
        doc.setFont(undefined, 'normal')
      }

      yPos += 3

      if (meterReadings.length > 0) {
        doc.setFontSize(8)
        doc.setFont(undefined, 'bold')
        doc.text('Date', margin, yPos)
        doc.text('Previous', margin + 30, yPos)
        doc.text('Current', margin + 55, yPos)
        doc.text('Inches', margin + 78, yPos)
        doc.text('Acre-In', margin + 98, yPos)
        doc.text('By', margin + 118, yPos)
        doc.text('Notes', margin + 145, yPos)
        yPos += 5
        doc.setFont(undefined, 'normal')

        const sortedReadings = [...meterReadings].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        for (let ri = 0; ri < sortedReadings.length; ri++) {
          const reading = sortedReadings[ri]
          const prevReading = ri > 0 ? sortedReadings[ri - 1] : null
          const rdateStr = reading.created_at ? new Date(reading.created_at).toLocaleDateString() : ''
          const acreInches = calculateAcreInches(reading.inches, meter.acres)

          let rowHeight = 5
          let photoHeight = 0
          let imageData = null

          if (includePhotos && photoUrlCache[reading.id]) {
            try {
              const imgResult = await loadImageAsBase64(photoUrlCache[reading.id])
              if (imgResult) {
                imageData = imgResult
                photoHeight = photoThumbnailWidth / imgResult.aspectRatio
                rowHeight = 5 + photoHeight + photoMargin * 2 + 2
                if (reading.notes && reading.notes.length > 20) {
                  const noteLines = Math.ceil(reading.notes.length / 60)
                  rowHeight += noteLines * 4 + 4
                }
              }
            } catch (e) {
              console.error('Error loading image for PDF:', e)
            }
          }

          if (yPos + rowHeight > 275) { doc.addPage(); yPos = 20 }

          const isSeasonStart = parseFloat(reading.start) === parseFloat(reading.end) && parseFloat(reading.inches) === 0
          doc.text(rdateStr, margin, yPos)
          doc.text(isSeasonStart ? '' : (formatReadingValue(reading.start, meter.units) || ''), margin + 30, yPos)
          doc.text(formatReadingValue(reading.end, meter.units) || '', margin + 55, yPos)
          doc.text(isSeasonStart ? '' : String(reading.inches || '0.00'), margin + 78, yPos)
          doc.text(isSeasonStart ? '' : String(acreInches || '0.00'), margin + 98, yPos)
          doc.text(String(reading.entered_by || '').substring(0, 15), margin + 118, yPos)
          const notesText = String(reading.notes || '')
          const notesColWidth = pageWidth - margin - (margin + 145)
          const noteLines = notesText ? doc.splitTextToSize(notesText, notesColWidth) : []
          if (noteLines.length > 0) doc.text(noteLines[0], margin + 145, yPos)

          let currentYPos = yPos

          if (imageData) {
            const photoX = margin + photoMargin
            const photoY = currentYPos + 5 + photoMargin
            doc.addImage(imageData.data, 'JPEG', photoX, photoY, photoThumbnailWidth - (photoMargin * 2), photoHeight - (photoMargin * 2))
            currentYPos += 5 + photoHeight + photoMargin
          }

          // Wrap remaining note lines below the row
          if (noteLines.length > 1) {
            currentYPos += 5
            for (let i = 1; i < noteLines.length; i++) {
              if (currentYPos > 270) { doc.addPage(); currentYPos = 20 }
              doc.text(noteLines[i], margin + 145, currentYPos)
              currentYPos += 4
            }
          }


          yPos = Math.max(yPos + rowHeight, currentYPos + 2)
        }
        yPos += 5
      } else {
        doc.setFontSize(9)
        doc.setFont(undefined, 'italic')
        doc.text('No readings recorded', margin, yPos)
        yPos += 8
      }

      yPos += 3

      if (!includePhotos && meterIndex < meters.length - 1) {
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, yPos, pageWidth - margin, yPos)
        yPos += 8
      }
    }

    doc.save(`${operation?.op_name || 'irrigation-report'}-${new Date().toISOString().split('T')[0]}.pdf`)
    setExportProgress(null)
    showToast('PDF exported successfully', 'success')
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (!session?.user) return

    ;(async () => {
      try {
        setLoading(true)
        const op = await ensureOperation(session.user)
        setOperation(op)
        setSeasonStart(op.season_start || '')
        setSeasonEnd(op.season_end || '')
        await Promise.all([
          loadMeters(op.id),
          loadReadings(op.id, op.season_start, op.season_end)
        ])
        // team members load in the operation-change effect below — loading them
        // here too fired the same request twice on startup
      } catch (e) {
        console.error("Setup error:", e)
        showToast(e.message || "Failed loading data", "error")
      } finally {
        setLoading(false)
      }
    })()

    if (session?.user?.user_metadata?.first_name) setEnteredBy(session.user.user_metadata.first_name)
  }, [session?.user?.id])

  useEffect(() => {
    if (!operation || !isOwner) return
    loadTeamMembers(operation.id, operation.owner_id)
  }, [operation?.id])

  useEffect(() => {
    if (!operation || !isOwner || !session?.user) return
    if (expandedSettingsSection === 'team') {
      loadOrCreateInviteCode(operation.id, session.user.id, operation.op_name)
    }
  }, [expandedSettingsSection])

  // Auto-show season carryover prompt on first visit after season start
  useEffect(() => {
    if (loading || !operation?.season_start || !isOwner || meters.length === 0) return

    const anyHasStartReading = meters.some(m => m.season_starting_reading !== null && m.season_starting_reading !== undefined)
    if (anyHasStartReading) return

    const today = new Date()
    const seasonStart = new Date(operation.season_start + 'T00:00:00')
    if (today < seasonStart) return

    const dismissKey = `season_prompt_dismissed_${operation.id}_${operation.season_start}`
    if (localStorage.getItem(dismissKey)) return

    setShowSeasonPrompt(true)
  }, [loading, operation?.id, operation?.season_start, meters, isOwner])

  // Refresh signed photo URLs every 50 minutes (before 60-min expiry)
  useEffect(() => {
    const hasPhotos = readings.some(r => r.photo_path)
    if (!hasPhotos) return
    const interval = setInterval(() => {
      loadAllReadingPhotoUrls(readings)
    }, 50 * 60 * 1000)
    return () => clearInterval(interval)
  }, [readings])

  useEffect(() => {
    if (!readingDateTime) {
      const now = new Date()
      setReadingDateTime(new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16))
    }
  }, [])

  const value = {
    // Session
    session,
    setSession,

    // Toast
    toast,
    showToast,

    // Core state
    loading,
    operation,
    meters,
    readings,
    isOwner,

    // Add Reading page
    mode,
    setMode,
    selectedMeterId,
    setSelectedMeterId,
    selectedMeter,
    lastReading,
    showSeasonStartCheckbox,
    markAsSeasonStart,
    setMarkAsSeasonStart,
    enteredBy,
    setEnteredBy,
    newReading,
    setNewReading,
    readingDateTime,
    setReadingDateTime,
    calcAcres,
    setCalcAcres,
    calcUnits,
    setCalcUnits,

    // Modals
    showAddMeter,
    setShowAddMeter,
    showQuickAddMeter,
    setShowQuickAddMeter,
    showRolloverModal,
    setShowRolloverModal,
    showDecimalConfirmModal,
    setShowDecimalConfirmModal,
    showSeasonStartConfirmModal,
    setShowSeasonStartConfirmModal,
    showSeasonPrompt,
    dismissSeasonPrompt,
    handleSeasonPromptCarryOver,
    showMeterDetails,
    setShowMeterDetails,
    meterDetailsId,
    setMeterDetailsId,
    photoPreviewModal,
    setPhotoPreviewModal,
    showExportModal,
    setShowExportModal,
    exportProgress,

    // Meter form
    newMeter,
    setNewMeter,
    resetNewMeter,
    editingMeterId,
    setEditingMeterId,
    editingMeter,
    setEditingMeter,

    // Meters page
    meterSearch,
    setMeterSearch,
    showMeterSearch,
    setShowMeterSearch,
    selectedMetric,
    setSelectedMetric,
    sortBy,
    setSortBy,
    showFilterControls,
    setShowFilterControls,
    filterUnits,
    setFilterUnits,
    expandedMeterId,
    setExpandedMeterId,

    // Settings page
    expandedSettingsSection,
    setExpandedSettingsSection,
    editingOpName,
    setEditingOpName,
    newOpName,
    setNewOpName,
    editingFirstName,
    setEditingFirstName,
    newFirstName,
    setNewFirstName,
    seasonStart,
    setSeasonStart,
    seasonEnd,
    setSeasonEnd,
    teamMembers,
    inviteCode,

    // Photos
    setReadingPhotoFile,
    readingPhotoPreview,
    setReadingPhotoPreview,
    readingPhotoUrls,
    handleReadingPhotoSelect,

    // Computed values
    getMeterReadings,
    getSeasonTotal,
    getLastIrrigation,
    getEntriesThisSeason,
    getLastReadingDate,
    isReadingOutsideSeason,

    // Actions
    handleAddMeter,
    handleEditMeter,
    handleDeleteMeter,
    handleUpdateSeasonStartReading,
    handleAddReading,
    saveReading,
    savingReading,
    saveFirstReading,
    handleDeleteReading,
    editingReadingId,
    setEditingReadingId,
    editingReading,
    setEditingReading,
    handleUpdateReading,
    handleUpdateOpName,
    handleUpdateSeasonDates,
    handleUpdateFirstName,
    confirmSeasonStartFromPreviousSeason,
    handleBulkSetSeasonStartFromPreviousSeason,
    backfillSeasonStartReadings,
    shareInviteLink,
    removeTeamMember,
    doLogout,
    handleExportCSV,
    handleExportPDF,
  }

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}
